import React from 'react';
import ChartComponent from '../ChartComponent';
import { ChartComponentDatasetType, DropdownOptionType, MemberDetailResponseType } from '../../types';
import { capitalizeFirstLetter } from '../../utils/texts';
import { useAttendanceColors } from '../../utils/hooks';
import Section from '../common/Section/Section';
import { useTranslation } from 'react-i18next';

type Props = {
    selectedAttendanceState: {
        value: string | number | null;
        setValue: (value: string | null) => void;
    };
    memberData: MemberDetailResponseType;
    selectedParty: DropdownOptionType | null;
};

const MemberDetailAttendanceSummaryChartSection = ({ selectedAttendanceState, memberData, selectedParty }: Props) => {
    const { t } = useTranslation();
    const { value: selectedAttendance, setValue: setSelectedAttendance } = selectedAttendanceState;
    const attendanceColors: { [key: string]: string } = useAttendanceColors();

    var hover_timeout: NodeJS.Timeout;

    const dataSource =
        selectedParty?.code === 0
            ? memberData.dochazka.sumarizace
            : memberData.votes_per_party.find((item) => item.party_id === selectedParty?.code)?.attendance_sum || {};

    let summaryAttendanceLabels = Object.keys(dataSource);
    let summaryAttendanceValues = Object.values(dataSource);

    const datasetsDataSummaryAttendance = Object.keys(dataSource).map((item, index) => {
        return {
            type: 'bar',
            label: capitalizeFirstLetter(item),
            backgroundColor: attendanceColors[item],
            data: [summaryAttendanceValues[index]]
        };
    });

    let summaryAttendanceData = {
        labels: [''],
        datasets: datasetsDataSummaryAttendance
    };

    const stackedOptionsAttendance = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        aspectRatio: 0.8,
        plugins: {
            tooltips: {
                mode: 'index',
                intersect: false
            },
            legend: {
                labels: {
                    generateLabels: (chart: { data: { datasets: ChartComponentDatasetType[] } }) => {
                        const datasets = chart.data.datasets;
                        return datasets.map((data: ChartComponentDatasetType, i: any) => ({
                            text: `${data.label} (${data.data[0]})`,
                            fillStyle: data.backgroundColor,
                            strokeStyle: 'white',
                            datasetIndex: 0,
                            index: i
                        }));
                    }
                },
                onHover: function (
                    evt: { native: { target: { style: { cursor: string } } } },
                    item: { index: any },
                    legend: {
                        chart: { tooltip: any; data: { datasets: ChartComponentDatasetType[] }; update: () => void };
                    }
                ) {
                    evt.native.target.style.cursor = 'pointer';
                    const tooltip = legend.chart.tooltip;
                    if (tooltip.getActiveElements().length > 0) {
                        tooltip.setActiveElements([], { x: 0, y: 0 });
                    }
                    tooltip.setActiveElements([{ datasetIndex: item.index, index: 0 }], { x: 0, y: 0 });
                    legend.chart.data.datasets.forEach((i, id) => {
                        if (id !== item.index && i.backgroundColor.length === 7) {
                            i.backgroundColor += '30';
                        }
                    });
                    clearTimeout(hover_timeout);
                    hover_timeout = setTimeout(function () {
                        legend.chart.update();
                    }, 60);
                },
                onLeave: function (
                    evt: any,
                    item: { index: any },
                    legend: {
                        chart: { tooltip: any; data: { datasets: ChartComponentDatasetType[] }; update: () => void };
                    }
                ) {
                    const tooltip = legend.chart.tooltip;
                    tooltip.setActiveElements([], { x: 0, y: 0 });
                    legend.chart.data.datasets.forEach((i, id) => {
                        if (id !== item.index && i.backgroundColor.length > 7) {
                            i.backgroundColor = i.backgroundColor.slice(0, -2);
                        }
                    });
                    legend.chart.update();
                }
            }
        },
        scales: {
            x: {
                stacked: true
            },
            y: {
                stacked: true,
                position: 'right'
            }
        },
        onHover: function (evt: { native: { target: { style: { cursor: string } } } }, item: undefined[]) {
            if (item[0] !== undefined) {
                evt.native.target.style.cursor = 'pointer';
            } else {
                evt.native.target.style.cursor = 'default';
            }
        },
        onClick: function (evt: any, item: { datasetIndex: string | number }[]) {
            if (item[0] !== undefined) {
                if (selectedParty?.code === 0) {
                    const currentLabel = summaryAttendanceLabels[item[0].datasetIndex as number];
                    setSelectedAttendance(currentLabel !== selectedAttendance ? currentLabel : null);
                } else {
                    setSelectedAttendance(item[0].datasetIndex as string);
                }
            }
        }
    };

    return (
        <>
            <Section title={t('member-attendance')} subtitle={t('tooltip-chart')}>
                <ChartComponent
                    type='bar'
                    data={summaryAttendanceData}
                    options={stackedOptionsAttendance}
                    height='200px'
                    width='100%'
                    xTitle={undefined}
                    yTitle={undefined}
                    aria={undefined}
                />
            </Section>
        </>
    );
};

export default MemberDetailAttendanceSummaryChartSection;
